import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { logError } from '@zg-rentals/log-error';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import PaymentSettingsPageV2 from './component';
import { getPaymentErrorInfo } from '../../../utils/getPaymentErrorInfo';
import { pageFlows } from '@renter-hub/pages/PaymentSettingsPage/pageFlows';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import type { CreatePaymentSubscriptionWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { CreatePaymentSubscriptionWebApiHandler_createPaymentSubscription } from '@zg-rentals/ts-rent-payment-client';
import { AUTO_FREQUENCY, MANUAL_FREQUENCY } from '@renter-hub/constants/paymentConstants';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_INSUFFICIENT_FUNDS,
  PAYMENT_PAID,
  PAYMENT_SCHEDULED_SUCCESS,
  PAYMENT_SCHEDULED_SUCCESS_DETAILED,
  PAYMENT_TIMEOUT,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import paths from '@renter-hub/pathsManifest';
import { REQUEST_TIMEOUT } from '../../../utils/customErrorTypes';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';

const mapStateToProps = (state, { fromDashboard, location }) => ({
  nextPayable:
    Object.keys(state.payments.renter.dashboard.nextPayable).length > 0
      ? state.payments.renter.dashboard.nextPayable
      : state.payments.renter.dashboard.nextScheduleMonth,
  paymentDetails: state.payments.renter.dashboard.paymentDetails,
  rentTerms: state.payments.renter.dashboard.rentTerms,
  startPage: state.router.location.state ? state.router.location.state.startPage : pageFlows.FrequencyPage,
  isMICPresent: state.payments.renter.dashboard.moveInCostsDetails?.items?.length > 0,
  fromDashboard,
  encodedPaymentMethodId: state.payments.renter.dashboard?.selectedPaymentMethod?.id,
  paymentMethods: state.payments.renter.dashboard.paymentMethods,
  pageFlowOverride: location?.state?.pageFlowOverride,
  savedFrequencyType: location?.state?.savedFrequencyType,
  user: state.user,
  listingAlias: state.payments?.renter?.dashboard?.listingAlias,
});

const mapDispatchToProps = (dispatch, { baseUrl, brand, fromDashboard, propertyId, location }) => ({
  closeNotification: () => dispatch(closeNotification()),
  requestInitiatePayment: async (requestPayload: CreatePaymentSubscriptionWebApiRequestDto, isOverdue?: boolean) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      await CreatePaymentSubscriptionWebApiHandler_createPaymentSubscription(requestPayload);

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate to the dashboard
      await dispatch(push(baseUrl));

      // Show the rent payment success banner
      if (fromDashboard) {
        if (isOverdue) {
          await dispatch(showPageNotification(PAYMENT_PAID));
        } else {
          await dispatch(
            showPageNotification(PAYMENT_SCHEDULED_SUCCESS_DETAILED, {
              dateOfTheMonth: formatDate(requestPayload.paymentDate, 'do'),
              date: formatDate(requestPayload.paymentDate, 'MMMM do, yyyy'),
              typeofPayment: requestPayload.recurring ? AUTO_FREQUENCY : MANUAL_FREQUENCY,
            }),
          );
        }
      } else {
        await dispatch(showModalNotification(PAYMENT_SCHEDULED_SUCCESS));
      }

      return { error: false };
    } catch (err) {
      const { httpStatus, errorCode } = getPaymentErrorInfo(err);

      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;

      // If 401 status, redirect to sign-in
      if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return null;
      }

      // If 408 status, show timeout message and redirect to dashboard
      if (httpStatus === HTTP_STATUS.SC_REQUEST_TIMEOUT) {
        logError({ error: err, errorType: REQUEST_TIMEOUT, context: { status: httpStatus, messageCode: errorCode } });

        await dispatch(push(baseUrl));
        notificationType = PAYMENT_TIMEOUT;
      }

      // If 422, show insufficient funds if message code is insufficient_funds
      if (httpStatus === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY && errorCode === ResponseMessageCode.INSUFFICIENT_FUNDS) {
        notificationType = PAYMENT_METHOD_INSUFFICIENT_FUNDS;
      }

      logError({ error: err, context: { notificationType } });
      const showNotification = fromDashboard ? showModalNotification : showPageNotification;
      dispatch(showNotification(notificationType));
      return { error: true };
    }
  },
  onAddNewPaymentClick: (savedFrequencyType) => {
    return dispatch(
      push(baseUrl + paths.editPaymentInfoPage, { fromWhere: paths.editPaymentSettingsPage, savedFrequencyType }),
    );
  },
  onBack: async (isMICPresent, user) => {
    await dispatch(closeNotification());
    let toUrl;
    if (fromDashboard || !isMICPresent) {
      if (!fromDashboard) {
        RachelCSEvents.emitClickGoToDashboardEvent({
          user,
          currentProperty: {},
          triggerObject: 'payment_settings_cadence_page',
        });
      }
      toUrl = baseUrl;
    } else {
      toUrl = baseUrl + paths.moveInCostsSchedulingPage;
    }
    await dispatch(push(toUrl));
  },
});

const mergeProps = (stateProps, dispatchProps) => {
  const { isMICPresent } = stateProps;
  return {
    ...stateProps,
    ...dispatchProps,
    onBack: () => dispatchProps.onBack(isMICPresent, stateProps.user),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PaymentSettingsPageV2);
